import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { API_BASE_URL, ROUTES } from '../constants';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [emailUnverified, setEmailUnverified] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [verifiedMessage, setVerifiedMessage] = useState('');
  const [verifiedError, setVerifiedError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isAdmin, login } = useAuth();

  useEffect(() => {
    const verified = searchParams.get('verified');
    if (verified === '1') setVerifiedMessage(t.login.emailVerifiedSuccess);
    else if (verified === 'expired') {
      setVerifiedError(t.login.emailVerifiedExpired);
      setEmailUnverified(true);
    }
    else if (verified === 'invalid') setVerifiedError(t.login.emailVerifiedInvalid);

    if (searchParams.get('reset') === '1') setResetSuccess(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const redirectTo = searchParams.get('redirect');
      if (redirectTo) {
        navigate(redirectTo);
      } else if (isAdmin) {
        navigate('/hub');
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, isAdmin, navigate, searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailUnverified(false);
    setResendMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      await axios.post(
        `${API_BASE_URL}/auth/login`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          withCredentials: true,
        }
      );

      await login();
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData?.detail === 'EMAIL_NOT_VERIFIED') {
        setEmailUnverified(true);
        return;
      }
      if (errorData?.detail) {
        if (Array.isArray(errorData.detail)) {
          const errorMessages = errorData.detail.map((e: any) =>
            `${e.loc?.join('.')}: ${e.msg}`
          ).join(', ');
          setError(errorMessages || t.login.validationError);
        } else {
          setError(errorData.detail);
        }
      } else {
        setError(t.login.loginFailed);
      }
    }
  };

  const handleResend = async () => {
    setResendMessage('');
    try {
      await axios.post(`${API_BASE_URL}/auth/resend-verification`, { email });
      setResendMessage(t.login.resendSuccess);
    } catch {
      setResendMessage(t.login.resendSuccess);
    }
  };

  return (
    <div className="login">
      <div className="login__container">
        <div className="login__card">
          <h1 className="login__title italic-title">{t.login.title}</h1>

          {resetSuccess && (
            <div className="login__success">
              {t.login.passwordResetSuccess}
            </div>
          )}

          {verifiedMessage && (
            <div className="login__success">
              {verifiedMessage}
            </div>
          )}

          {verifiedError && !emailUnverified && (
            <div className="login__error">
              {verifiedError}
            </div>
          )}

          {error && (
            <div className="login__error">
              {error}
            </div>
          )}

          {emailUnverified && (
            <div className="login__error">
              {verifiedError || t.login.emailNotVerified}
              <button
                type="button"
                className="login__resend-btn"
                onClick={handleResend}
              >
                {t.login.resendVerification}
              </button>
              {resendMessage && (
                <div className="login__resend-success">{resendMessage}</div>
              )}
            </div>
          )}

          <form className="login__form" onSubmit={handleSubmit}>
            <div className="login__field">
              <label htmlFor="email" className="login__label">{t.login.email}</label>
              <input
                id="email"
                className="login__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="login__field">
              <label htmlFor="password" className="login__label">{t.login.password}</label>
              <input
                id="password"
                className="login__input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Link to={ROUTES.FORGOT_PASSWORD} className="login__forgot-link">
                {t.login.forgotPassword}
              </Link>
            </div>
            <button
              type="submit"
              className="login__submit button_primary"
            >
              {t.login.submit}
            </button>
            <GoogleSignInButton className="home__google-button" />
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
